import React from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TableRowSelection } from 'antd/es/table/interface';
import moment from 'moment';
import styled from 'styled-components';
import { ITaskData } from './types';
import { ArrowUpRight, CheckCircle } from 'lucide-react';
import logger from '../../utils/logger';

const StyledTable = styled(Table)`
  font-family: 'Gilroy', sans-serif;
  background: white;
  border-radius: 12px;

  // Remove ALL possible border radius
  * {
    border-radius: 0 !important;
  }

  .ant-table {
    background: transparent;
    border-spacing: 0 !important;
    border-collapse: collapse !important;
  }

  // Header styling
  .ant-table-thead {
    tr th {
      height: 24px !important;
      padding: 10px 16px !important;
      background: #f8fafd !important;
      font-weight: 400 !important;
      color: #64748b !important;
      text-align: left !important;
      border-bottom: 1px solid #edf2f7 !important;
      font-size: 12px !important;
      letter-spacing: 0.3px;
      text-transform: capitalize;

      &::before {
        display: none !important;
      }
    }
  }

  // Body styling with specific column weights
  .ant-table-tbody {
    > tr > td {
      height: 24px !important;
      padding: 8px 16px !important;
      background: white !important;
      text-align: left !important;
      border-bottom: 1px solid #f1f5f9 !important;
      color: #334155 !important;
      font-size: 13px !important;
      font-weight: 400 !important;

      // Lead name column - bold and larger
      &:nth-child(2) {
        font-weight: 700 !important;
        font-size: 15px !important;
        color: #1e293b !important;
      }

      // Due time column - bold and larger
      &:nth-child(4) {
        font-weight: 700 !important;
        font-size: 15px !important;
        color: #1e293b !important;
      }
    }

    // Subtle hover
    > tr:hover > td {
      background: #fafbff !important;
    }

    // Very subtle zebra striping
    > tr:nth-child(even) > td {
      background: #fafbfd !important;
    }
  }

  // Selection column
  .ant-table-selection-column {
    padding-left: 16px !important;
    width: 40px !important;
  }

  // Checkbox refinements
  .ant-checkbox-wrapper {
    .ant-checkbox-inner {
      border-color: #e2e8f0 !important;
      width: 14px !important;
      height: 14px !important;
      
      &:hover {
        border-color: #60a5fa !important;
      }
    }

    .ant-checkbox-checked .ant-checkbox-inner {
      background-color: #3b82f6 !important;
      border-color: #3b82f6 !important;
    }
  }

  // Lead Name Cell with Row-wide Hover Actions
  .lead-name-cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 48px;
  }

  .lead-name-text {
    line-height: 1.4;
    flex: 1;
    margin-right: 12px;
    font-weight: 700;
    font-size: 15px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }

  .lead-name-actions {
    display: flex;
    gap: 6px;
    opacity: 0;
    transition: opacity 0.2s ease;
    flex-shrink: 0;
  }

  // Show icons when hovering over the entire row
  .ant-table-tbody tr:hover .lead-name-actions {
    opacity: 1;
  }

  .lead-action-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    background: rgba(100, 116, 139, 0.15);
    color: #1e293b;

    * {
      pointer-events: none;
    }

    &:hover {
      background: rgba(100, 116, 139, 0.25);
      color: #0f172a;
      transform: scale(1.05);
    }

    &.complete-icon {
      background: rgba(34, 197, 94, 0.15);
      color: #047857;

      &:hover {
        background: rgba(34, 197, 94, 0.25);
        color: #065f46;
      }
    }
  }

  // Notes Column Styling - Show full notes
  .notes-cell {
    max-width: 300px;
  }

  .notes-text {
    line-height: 1.4;
    word-break: break-word;
    white-space: pre-wrap;
  }

  // Maintain scroll behavior
  .ant-table-container {
    min-height: 0 !important;
  }
  
  .ant-table-body {
    min-height: 0 !important;
    max-height: none !important;
  }

  // Row height - increased for larger action buttons
  .ant-table-row {
    height: 48px !important;
  }

  // Refined scrollbar
  .ant-table-body::-webkit-scrollbar {
    width: 5px !important;
  }

  .ant-table-body::-webkit-scrollbar-track {
    background: #f8fafc !important;
  }

  .ant-table-body::-webkit-scrollbar-thumb {
    background: #e2e8f0 !important;
    border-radius: 20px !important;
    
    &:hover {
      background: #cbd5e0 !important;
    }
  }

  // Ensure no rounded corners anywhere
  .ant-table-content,
  .ant-table-row,
  .ant-table-row td,
  .ant-table-row:last-child td {
    border-radius: 0 !important;
  }
`;

interface TaskGridProps {
  data: ITaskData[];
  isLoading: boolean;
  handleEditTask: (_id: string) => void;
  selectedRowKeys: React.Key[];
  onSelectChange: (newSelectedRowKeys: React.Key[], selectedRows: ITaskData[]) => void;
  
  // New props for direct actions
  onLeadDetailsClick?: (leadId: string, leadName: string) => void;
  onCompleteTaskClick?: (taskId: string, leadName: string) => void;
  currentTab?: "today" | "upcoming" | "overdue" | "completed";
}

const TaskGrid: React.FC<TaskGridProps> = ({
  data,
  isLoading,
  handleEditTask,
  selectedRowKeys,
  onSelectChange,
  onLeadDetailsClick,
  onCompleteTaskClick,
  currentTab = "today"
}) => {

  const columns: ColumnsType<ITaskData> = [
    {
      title: "Lead Name",
      key: "leadName",
      render: (record: ITaskData) => {
        const leadName = record.lead?.[0]?.name || '-';
        const leadId = record.lead?.[0]?._id;
        const showCompleteIcon = (currentTab === "today" || currentTab === "overdue") && !record.isCompleted;
        
        return (
          <div className="lead-name-cell">
            <div className="lead-name-text">{leadName}</div>
            {leadId && (
              <div className="lead-name-actions">
                {/* Lead Details Icon */}
                <button
                  className="lead-action-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    logger.log('🔍 Lead details icon clicked');
                    onLeadDetailsClick?.(leadId, leadName);
                  }}
                  title={`View ${leadName}'s details`}
                >
                  <ArrowUpRight size={30} />
                </button>
                
                {/* Complete Task Icon */}
                {showCompleteIcon && (
                  <button
                    className="lead-action-icon complete-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      logger.log('✅ Complete icon clicked');
                      onCompleteTaskClick?.(record._id, leadName);
                    }}
                    title="Mark task as complete"
                  >
                    <CheckCircle size={30} />
                  </button>
                )}
              </div>
            )}
          </div>
        );
      },
      align: 'left',
      width: '22%'
    },
    {
      title: "Task owner",
      dataIndex: "createdBy",
      key: "taskOwner",
      align: 'left',
      width: '15%'
    },
    {
      title: "Due time",
      key: "dueTime",
      render: (record: ITaskData) => 
        record.toBePerformAt ? 
        moment.utc(record.toBePerformAt).add(5, 'hours').add(30, 'minutes').format("DD MMM YYYY, hh:mm a") : 
        '-',
      align: 'left',
      width: '20%',
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type: string) => type?.toLowerCase() || '-',
      align: 'left',
      width: '12%'
    },
    {
      title: "Assigned to",
      key: "assignedTo",
      render: (record: ITaskData) => {
        if (!record.assignedTo?.length) return 'N/A';
        return record.assignedTo[0]?.name || 'N/A';
      },
      align: 'left',
      width: '15%'
    },
    {
      title: "Note",
      key: "note",
      render: (record: ITaskData) => {
        const notes = record.notes;
        
        return (
          <div className="notes-cell">
            <span className="notes-text">
              {notes || 'N/A'}
            </span>
          </div>
        );
      },
      align: 'left',
      width: '25%'  // More space for full notes
    }
  ];

  const rowSelection: TableRowSelection<ITaskData> = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: false,
    columnWidth: 48
  };

  return (
    <>
      <StyledTable
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
        loading={isLoading}
        pagination={false}
        rowKey="_id"
        scroll={{ y: undefined }}
        onRow={(record: ITaskData) => ({
          onClick: (e) => {
            // Only trigger edit if not clicking on action buttons or notes expand
            const target = e.target as HTMLElement;
            const isActionClick = target.closest('.lead-action-icon') || 
                                target.closest('.notes-expand-btn');
            
            if (!isActionClick) {
              logger.log('📝 Opening task edit for:', record._id);
              handleEditTask(record._id);
            }
          },
          style: { cursor: 'pointer' }
        })}
      />

    
    </>
  );
};

export default TaskGrid;